import { POSITION_ATTRIBUTES } from '../api/constants';
import { extractAttributes } from '../api/positionApi';

export const grantDataViewAccess = async (engine, userId, managerOrgUnitId) => {
  try {
    console.log('🔄 [GRANT ACCESS] Starting for user:', userId, 'to org unit:', managerOrgUnitId);
    
    // 1. Fetch COMPLETE user data
    const { user: currentUser } = await engine.query({
      user: {
        resource: `users/${userId}`,
        params: { 
          fields: 'id,firstName,surname,username,email,phoneNumber,organisationUnits[id],dataViewOrganisationUnits[id],userRoles[id]' 
        }
      }
    });

    console.log('📊 [CURRENT USER DATA]', {
      firstName: currentUser.firstName,
      surname: currentUser.surname,
      username: currentUser.username,
      orgUnits: currentUser.organisationUnits?.map(ou => ou.id),
      dataViewUnits: currentUser.dataViewOrganisationUnits?.map(ou => ou.id),
      userRoles: currentUser.userRoles?.map(role => role.id)
    });

    // 2. Check if already has data view access
    const alreadyHasDataView = currentUser.dataViewOrganisationUnits?.some(ou => ou.id === managerOrgUnitId);
    
    if (alreadyHasDataView) {
      console.log('✅ [SKIP] User already has data view access to org unit');
      return true;
    }

    // 3. Build updated arrays
    const currentOrgUnits = (currentUser.organisationUnits || []).map(ou => ({ id: ou.id }));
    const currentDataView = (currentUser.dataViewOrganisationUnits || []).map(ou => ({ id: ou.id }));
    const currentUserRoles = (currentUser.userRoles || []).map(role => ({ id: role.id }));

    // Add manager org unit to dataViewOrganisationUnits only
    currentDataView.push({ id: managerOrgUnitId });

    // 4. Build payload
    const updatePayload = {
      id: userId,
      firstName: currentUser.firstName || '',
      surname: currentUser.surname || '',
      username: currentUser.username || '',
      email: currentUser.email,                
      phoneNumber: currentUser.phoneNumber,    
      organisationUnits: currentOrgUnits,      // Unchanged
      dataViewOrganisationUnits: currentDataView,  // Updated with new data view org unit
      userRoles: currentUserRoles
    };

    console.log('📤 [UPDATE PAYLOAD]', JSON.stringify(updatePayload, null, 2));

    // 5. Perform the update
    const result = await engine.mutate({
      resource: `users/${userId}`,
      type: 'update',
      data: updatePayload
    });

    console.log('✅ [SUCCESS] Update result:', result);
    
    if (result?.httpStatusCode === 200 || result?.status === 'OK') {
      console.log('✅ Data view access granted successfully');
      return true;
    } else {
      console.warn('⚠️ Update returned with non-OK status:', result);
      return false;
    }
    
  } catch (error) {
    console.error('❌ [ERROR] Failed to grant access:', {
      message: error.message,
      details: error.details || error.response || error,
      stack: error.stack
    });
    return false;
  }
};

export const manageReportingAccess = async (engine, positionsList, positionTei, staffId, reportingToTei) => {
  if (!staffId || !reportingToTei) return false;
  
  try {
    // Find the reporting position
    const reportingPosition = positionsList.find(p => 
      p.trackedEntityInstance === reportingToTei
    );
    
    if (!reportingPosition?.orgUnit) {
      console.warn('No org unit found for reporting position');
      return false;
    }
    
    // 🎯 NEW: Get the reporting manager's details
    const reportingAttrs = extractAttributes(reportingPosition.attributes);
    const reportingManagerId = reportingAttrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
    
    if (!reportingManagerId) {
      console.warn('Reporting position has no current incumbent');
      return false;
    }
    
    // 🎯 NEW: Grant access to manager's org unit chain (optional)
    // This would allow seeing data up the hierarchy
    const managerAccessGranted = await grantHierarchicalDataViewAccess(
      engine, 
      staffId, 
      reportingPosition.orgUnit
    );
    
    return managerAccessGranted;
    
  } catch (error) {
    console.error('Error managing reporting access:', error);
    return false;
  }
};

// Helper function for hierarchical access
const grantHierarchicalDataViewAccess = async (engine, userId, orgUnitId) => {
  // Fetch the org unit hierarchy
  const { organisationUnit } = await engine.query({
    organisationUnit: {
      resource: `organisationUnits/${orgUnitId}`,
      params: { fields: 'id,name,path,level' }
    }
  });
  
  // Extract all org units in the path (for hierarchical access)
  const pathIds = organisationUnit.path?.split('/').filter(id => id) || [];
  
  // Grant data view access to the entire chain
  const success = await grantDataViewAccess(engine, userId, orgUnitId);
  
  console.log(`✅ Hierarchical access: ${pathIds.length} levels available`);
  return success;
};